import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CountUp from "react-countup";
import { motion } from "framer-motion";
import {
  MagnifyingGlass, Kanban, Rows, TrendUp, Stack, FileMagnifyingGlass, Timer, Plus,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { money, moneyShort, relativeTime, daysLeft } from "../../lib/format";
import { PIPELINE_ORDER, stageMeta, STAGES, TERMINAL } from "../../lib/stages";
import { StatusChip } from "../../components/StatusChip";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

function StatCard({ icon: Icon, label, value, prefix, delay, isMoney }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="rounded-[var(--radius)] border border-border bg-card card-inner-glow p-5 relative overflow-hidden"
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
    >
      <div className="flex items-center justify-between">
        <span className="overline text-muted-foreground">{label}</span>
        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon size={17} weight="duotone" className="text-primary" />
        </div>
      </div>
      <div className="mt-4 num text-3xl font-semibold tracking-tight">
        {prefix}
        <CountUp end={value} duration={1.1} separator="," formattingFn={isMoney ? (v) => money(v) : undefined} />
      </div>
    </motion.div>
  );
}

function DealCard({ deal, onClick, index }) {
  const meta = stageMeta(deal.stage);
  const dl = daysLeft(deal.terms?.offer_expiration);
  const dlColor = dl == null ? "#94A3B8" : dl <= 2 ? "#F87171" : dl <= 5 ? "#FBBF24" : "#10B981";
  const { approved, total } = deal.doc_summary || { approved: 0, total: 0 };
  return (
    <motion.button
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.03 }}
      onClick={onClick}
      data-testid={`deal-card-${deal.id}`}
      className="w-full text-left rounded-xl border border-border bg-card hover:-translate-y-0.5 hover:border-primary/30 transition-all duration-200 p-4 card-inner-glow group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-display font-semibold text-sm leading-tight group-hover:text-primary transition-colors">
          {deal.business?.dba || deal.business?.legal_name}
        </div>
      </div>
      <div className="num text-xl font-semibold mt-2">{money(deal.terms?.credit_limit)}</div>
      <div className="mt-3 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">
          {approved}/{total} docs
        </span>
        {deal.stage !== "funded" && deal.stage !== "declined" && dl != null && (
          <span className="num font-medium" style={{ color: dlColor }}>
            {dl < 0 ? "Expired" : `${dl}d left`}
          </span>
        )}
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-primary transition-all" style={{ width: total ? `${(approved / total) * 100}%` : "0%" }} />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{relativeTime(deal.last_activity_at)}</span>
        <StatusChip label={meta.label} color={meta.color} bg={meta.bg} />
      </div>
    </motion.button>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [deals, setDeals] = useState(null);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [stageFilter, setStageFilter] = useState("all");

  const load = () => {
    api.get("/deals").then((r) => setDeals(r.data)).catch(() => setDeals([]));
    api.get("/deals/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!deals) return [];
    return deals.filter((d) => {
      const name = (d.business?.legal_name || "") + (d.business?.dba || "") + (d.business?.owner_name || "");
      const matchesQ = !q || name.toLowerCase().includes(q.toLowerCase());
      const matchesStage = stageFilter === "all" || d.stage === stageFilter;
      return matchesQ && matchesStage;
    });
  }, [deals, q, stageFilter]);

  const columns = [...STAGES, ...TERMINAL];

  return (
    <div className="p-5 md:p-8 max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline text-primary">Deal Pipeline</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Origination Console</h1>
        </div>
        <Button data-testid="new-deal-button" onClick={() => nav("/admin/deals/new")} className="font-semibold self-start md:self-auto">
          <Plus size={18} className="mr-1" /> New Deal
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-6">
        {stats ? (
          <>
            <StatCard icon={Stack} label="Active Deals" value={stats.total_active} delay={0} />
            <StatCard icon={TrendUp} label="Approved Volume" value={stats.approved_volume} delay={0.05} isMoney />
            <StatCard icon={FileMagnifyingGlass} label="Awaiting Review" value={stats.awaiting_review} delay={0.1} />
            <StatCard icon={Timer} label="Expiring < 72h" value={stats.expiring_soon} delay={0.15} />
          </>
        ) : (
          [0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-[var(--radius)] border border-border bg-card p-5 h-[116px] animate-pulse" />
          ))
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row gap-3 mt-8 mb-5">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="deal-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by business or owner…"
            className="pl-10 bg-card border-border h-10"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-full md:w-48 bg-card border-border h-10" data-testid="stage-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {columns.map((s) => (
              <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex rounded-xl border border-border bg-card p-1">
          <button
            data-testid="view-kanban"
            onClick={() => setView("kanban")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === "kanban" ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}
          >
            <Kanban size={17} /> Board
          </button>
          <button
            data-testid="view-table"
            onClick={() => setView("table")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === "table" ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}
          >
            <Rows size={17} /> Table
          </button>
        </div>
      </div>

      {/* Content */}
      {deals === null ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-64 rounded-xl bg-card border border-border animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState onCreate={() => nav("/admin/deals/new")} />
      ) : view === "kanban" ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {columns.map((col) => {
            const items = filtered.filter((d) => d.stage === col.key);
            const meta = stageMeta(col.key);
            return (
              <div key={col.key} className="w-72 shrink-0" data-testid={`column-${col.key}`}>
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: meta.color }} />
                    <span className="text-sm font-semibold">{col.label}</span>
                  </div>
                  <span className="num text-xs text-muted-foreground bg-secondary rounded-full px-2 py-0.5">{items.length}</span>
                </div>
                <div className="space-y-3">
                  {items.map((d, i) => (
                    <DealCard key={d.id} deal={d} index={i} onClick={() => nav(`/admin/deals/${d.id}`)} />
                  ))}
                  {items.length === 0 && (
                    <div className="rounded-xl border border-dashed border-border h-20 flex items-center justify-center text-xs text-muted-foreground">
                      No deals
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium overline">Business</th>
                <th className="px-4 py-3 font-medium overline">Limit</th>
                <th className="px-4 py-3 font-medium overline">Stage</th>
                <th className="px-4 py-3 font-medium overline">Docs</th>
                <th className="px-4 py-3 font-medium overline">Deadline</th>
                <th className="px-4 py-3 font-medium overline">Owner</th>
                <th className="px-4 py-3 font-medium overline">Activity</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const meta = stageMeta(d.stage);
                const dl = daysLeft(d.terms?.offer_expiration);
                return (
                  <tr
                    key={d.id}
                    data-testid={`deal-row-${d.id}`}
                    onClick={() => nav(`/admin/deals/${d.id}`)}
                    className="border-b border-border/60 last:border-0 hover:bg-secondary/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 font-medium">{d.business?.dba || d.business?.legal_name}</td>
                    <td className="px-4 py-3 num">{moneyShort(d.terms?.credit_limit)}</td>
                    <td className="px-4 py-3"><StatusChip label={meta.label} color={meta.color} bg={meta.bg} /></td>
                    <td className="px-4 py-3 num text-muted-foreground">{d.doc_summary?.approved}/{d.doc_summary?.total}</td>
                    <td className="px-4 py-3 num text-muted-foreground">{dl == null ? "—" : dl < 0 ? "Expired" : `${dl}d`}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.assigned_to_name}</td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">{relativeTime(d.last_activity_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="rounded-[var(--radius)] border border-dashed border-border bg-card/50 py-20 flex flex-col items-center text-center px-6">
      <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
        <Stack size={26} weight="duotone" className="text-primary" />
      </div>
      <h3 className="font-display text-xl font-semibold">No deals yet</h3>
      <p className="text-muted-foreground text-sm mt-1 max-w-sm">
        Build your first line of credit deal and generate a secure merchant link in minutes.
      </p>
      <Button className="mt-5" onClick={onCreate} data-testid="empty-create-deal">
        <Plus size={18} className="mr-1" /> Create a deal
      </Button>
    </div>
  );
}
